import ColorSwatch from 'src/common-ui/components/ColorSwatch/ColorSwatch';
import { concat, noop, get, isArray, isNil, isEmpty } from 'lodash';
import { cloneDeep } from 'lodash/fp';
import React from 'react';
import { Button, createStyles, TextField, Theme, WithStyles, withStyles } from '@material-ui/core';
import { CartItem, CartItemType, ColorSwatchItem } from '../AssortmentCart.types';
import { classes, style } from 'typestyle';
import { getSwatchUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import styles, { swatchStyles } from './ColorsEdit.styles';
import { watermarkImageAsDataURI } from 'src/utils/Images/WatermarkImage';
import { resolvePath } from 'src/cdn';
import { ISASSORTMENT, CCCOLORID, IMG_URI } from 'src/utils/Domain/Constants';
import { getStyleColorsAttr } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.client';
import noImagePath from 'src/common-ui/images/noimage.jpg';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { Autocomplete } from '@material-ui/lab';
import {
  baseAutocompleteStyles,
  generalPopper,
  generalPopupIndicator,
} from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest.styles';
import { KeyboardArrowDown } from '@material-ui/icons';
import { ListboxVirtualized } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import type { AppState } from 'src/reducers';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { connect } from 'react-redux';
import { important } from 'csx';

const noImage = resolvePath(noImagePath);

type Props = ValueProps & FunctionProps;
type ValueProps = {
  item: CartItem;
  colors: string[];
  title: string;
  placeholderSrc: string;
  portalRef?: React.RefObject<HTMLElement>;
  canAddNewStyles: boolean;
};
type FunctionProps = {
  getStylePlaceholder: (styleId: string) => string;
  changeSwatches?: (swatches: ColorSwatchItem[]) => void;
  closeModal: () => void;
};
export type SelectedOption = {
  label: string;
  value: string;
  isExisting?: boolean;
};
const ADD_COLOR_LIST_WIDTH = 300;
type State = {
  selectedColorId: string | null;
  newSwatches: ColorSwatchItem[];
  existingSwatches: ColorSwatchItem[];
  assortmentSwatches: ColorSwatchItem[];
  selectedNewColor: SelectedOption | null;
  placeholderCoveredSrc: string;
  colorsInProduct: ColorSwatchItem[];
};
const muistyles = (theme: Theme) => {
  return createStyles({
    paper: {
      width: theme.spacing(50),
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(4),
    },
  });
};

interface StyleIProps extends WithStyles<typeof muistyles>, Props {
  placeholderImgAttr?: string;
}

const renderOption = (option: SelectedOption) => (
  <div style={{ color: option.isExisting ? 'blue' : 'black' }}>{option.label || option.value}</div>
);

class ColorsEdit extends React.Component<StyleIProps, State> {
  state: State;

  constructor(props: StyleIProps) {
    super(props);

    this.state = {
      selectedColorId: null,
      newSwatches: [],
      existingSwatches: [],
      assortmentSwatches: [],
      selectedNewColor: null,
      placeholderCoveredSrc: props.placeholderSrc || '',
      colorsInProduct: [],
    };
  }

  setSwatchStateFromProps = () => {
    const asstSw = this.props.item.currentAssortmentChildren
      ? this.props.item.currentAssortmentChildren.filter((_i) => {
          return this.props.item.type === CartItemType.existing;
        })
      : [];

    const existSw =
      this.props.item.type === CartItemType.existing || this.props.item.type === CartItemType.similar
        ? this.props.item.currentChildren
        : [];

    this.setState({
      selectedColorId: get(this.props.item.swatches, '[0]["attribute:cccolor:id"]', null),
      newSwatches: this.props.item.swatches,
      assortmentSwatches: cloneDeep(asstSw),
      existingSwatches: cloneDeep(existSw),
    });
  };

  fetchAllSwatches = async () => {
    const everyProductInStyle = await getStyleColorsAttr(this.props.item.id, true);
    const colorsInProduct = everyProductInStyle.map((prod) => {
      return {
        type: 'existing',
        swatchPath: '',
        colorId: prod['attribute:cccolor:id'],
        ...prod,
      };
    });
    this.setState({
      colorsInProduct: colorsInProduct,
    });
  };

  async componentDidMount() {
    this.setSwatchStateFromProps();
    await this.createPlaceholder();
    this.fetchAllSwatches();
    if (this.state.newSwatches.length <= 0) {
      this.addColor();
    }
  }

  async createPlaceholder() {
    const placeholderImgAttr = this.props.placeholderImgAttr;
    let imgSrc: string | null = null;
    if (!isNil(placeholderImgAttr)) {
      imgSrc = (this.props.item as unknown as BasicPivotItem)[placeholderImgAttr];
    } else {
      imgSrc = await watermarkImageAsDataURI(this.props.placeholderSrc).catch(() => watermarkImageAsDataURI(noImage));
    }
    this.setState({
      placeholderCoveredSrc: imgSrc || noImage,
    });
  }

  componentDidUpdate(oldProps: StyleIProps) {
    if (oldProps.item.id !== this.props.item.id && oldProps.item._id !== this.props.item._id) {
      this.setSwatchStateFromProps();
    }
    if (oldProps.placeholderSrc !== this.props.placeholderSrc) {
      this.createPlaceholder();
    }
  }

  getColors = () => {
    return this.state.newSwatches;
  };

  getSelectedColor = () => {
    const selectedColor: ColorSwatchItem | undefined = this.getColors().find(
      (col) => col.colorId === this.state.selectedColorId,
    );
    return selectedColor;
  };
  getSelectedInd = () => {
    const selectedColor: number | undefined = this.getColors().findIndex(
      (col) => col.colorId === this.state.selectedColorId,
    );
    return selectedColor || 0;
  };
  buildSwatches = () => {
    const swatches = this.getColors().map((swatch) => {
      return {
        swatchPath: '',
        id: swatch.colorId || '',
        colorId: swatch.colorId,
        selected: this.getSelectedColor() === swatch,
        noImageUrl: '',
      };
    });
    return (
      <ColorSwatch
        noImageUrl={''}
        className={swatchStyles}
        style={{ display: 'flex', overflowX: 'auto', marginTop: 5, maxWidth: 200 }}
        onChangeColor={(id: string, _position: number) => {
          this.setState({
            selectedColorId: id,
          });
        }}
        colors={swatches}
        selected={this.getSelectedInd()}
        getSwatchUrl={getSwatchUrl}
        renderTooltip={true}
      />
    );
  };
  buildExistingSwatches = () => {
    const swatches = this.state.assortmentSwatches.map((swatch) => {
      return {
        swatchPath: '',
        id: swatch.colorId || '',
        colorId: swatch.colorId,
        selected: this.getSelectedColor() === swatch,
        noImageUrl: '',
      };
    });
    return (
      <ColorSwatch
        noImageUrl={''}
        className={swatchStyles}
        style={{ display: 'inline-block', overflowX: 'auto' }}
        onChangeColor={noop}
        colors={swatches}
        selected={-1}
        getSwatchUrl={getSwatchUrl}
        renderTooltip={true}
      />
    );
  };
  getExistingChild = (colorId: string) => {
    return this.state.colorsInProduct.find((child) => {
      return child[CCCOLORID] === colorId;
    });
  };
  inExistingChildren = (colorId: string) => {
    return this.getExistingChild(colorId) != null;
  };
  addColor = async () => {
    const selectedNewColor = this.state.selectedNewColor;
    if (selectedNewColor == null) {
      return noop();
    }
    const newColorValue = selectedNewColor.value;
    const passedStyle = this.props.item;
    const colorExists = this.getExistingChild(newColorValue);
    const copiedColor: ColorSwatchItem =
      this.getExistingChild(newColorValue) || this.getSelectedColor() || this.props.item.currentChildren[0];
    let imageSrc: string | null | undefined = null;
    let isWatermarked = false;
    if (colorExists) {
      if (this.props.item.type === CartItemType.existing) {
        imageSrc = copiedColor[IMG_URI];
      } else {
        if (!isNil(this.props.placeholderImgAttr)) {
          imageSrc = (copiedColor as unknown as BasicPivotItem)[this.props.placeholderImgAttr] ?? noImage;
        } else {
          isWatermarked = true;
          imageSrc = await watermarkImageAsDataURI(copiedColor[IMG_URI]!).catch(() => watermarkImageAsDataURI(noImage));
        }
      }
    } else {
      if (!isNil(this.props.placeholderImgAttr)) {
        imageSrc = (copiedColor as unknown as BasicPivotItem)[this.props.placeholderImgAttr] ?? noImage;
      } else {
        imageSrc = this.state.placeholderCoveredSrc;
        isWatermarked = true;
      }
    }
    const newItem = {
      ...copiedColor,
      name: `${passedStyle.name}-${newColorValue.split(' ')[0]}`,
      description: `${passedStyle.description} ${newColorValue}`,
      colorId: selectedNewColor.value,
      [ISASSORTMENT]: 'false',
      [CCCOLORID]: newColorValue,
      ['attribute:cccolorid:id']: newColorValue.split(' ')[0],
      'attribute:img:id': imageSrc || this.state.placeholderCoveredSrc,
      isWatermarked,
      type:
        colorExists && this.props.item.type === CartItemType.existing ? CartItemType.existing : CartItemType.similar,
      swatchPath: getSwatchUrl(newColorValue),
    };

    const newItems: ColorSwatchItem[] = concat(this.getColors(), [newItem]);

    this.setState({
      newSwatches: newItems,
      selectedNewColor: null,
      selectedColorId: newColorValue,
    });
  };
  removeColor = () => {
    const selectedColor = this.getSelectedColor();
    const newItems = this.getColors().filter((swatch) => swatch !== selectedColor);
    this.setState({
      newSwatches: newItems,
      selectedColorId: get(newItems, '[0]["attribute:cccolor:id"]', null),
    });
  };

  onClickUpdate = () => {
    if (this.props.changeSwatches && !isEmpty(this.state.newSwatches)) {
      this.props.changeSwatches(this.state.newSwatches);
    }
  };

  renderAddColorList = () => {
    const existColorInProduct = this.state.colorsInProduct.filter((item) =>
      this.state.existingSwatches.find((item2) => item2[CCCOLORID] === item[CCCOLORID]),
    );
    const options = this.props.colors
      .filter((color) => {
        return (
          this.state.newSwatches.map((s) => s.colorId).indexOf(color) < 0 &&
          this.state.assortmentSwatches.map((s) => s.colorId).indexOf(color) < 0
        );
      })
      .filter((color) => {
        if (this.props.canAddNewStyles === false) {
          return this.inExistingChildren(color);
        } else {
          return true;
        }
      })
      .filter((color) =>
        this.props.canAddNewStyles ? color : existColorInProduct.find((item) => item[CCCOLORID] === color),
      )
      .map((color) => {
        const displayName: string = color;
        const isExisting = this.inExistingChildren(color);
        return {
          value: color,
          label: displayName,
          isExisting,
        };
      });

    const overrides = {
      autocomplete: {
        input: style({
          $debugName: 'colors-edit-input-overrides',
          padding: important(0),
        }),
      },
    };

    return (
      <div className={styles.autocompleteContainer} data-qa="add-color-dropdown">
        <Autocomplete
          popupIcon={<KeyboardArrowDown />}
          options={options}
          getOptionLabel={(option) => option.label || option.value}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={(_event, value) => {
            if (!isArray(value)) {
              this.setState({
                selectedNewColor: value,
              });
            }
          }}
          value={this.state.selectedNewColor || undefined}
          forcePopupIcon
          selectOnFocus
          disableClearable
          openOnFocus
          blurOnSelect
          classes={{
            ...baseAutocompleteStyles,
            popupIndicator: generalPopupIndicator,
            paper: generalPopper,
            popper: style({
              minWidth: `${ADD_COLOR_LIST_WIDTH}px`,
              marginLeft: '89px',
            }),
            input: classes(baseAutocompleteStyles.input, overrides.autocomplete.input),
          }}
          renderOption={renderOption}
          ListboxProps={{
            maxListHeight: 220,
            highlightedIndex: -1,
            listWidth: ADD_COLOR_LIST_WIDTH,
          }}
          ListboxComponent={ListboxVirtualized}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select an option" />}
        />
      </div>
    );
  };

  closeModal() {
    this.props.closeModal();
  }

  render() {
    const { placeholderSrc } = this.props;
    const selectedColor = this.getSelectedColor();
    // newly added swatch colors need to display placeholder image
    const selectedColorImgSrc = !isNil(selectedColor)
      ? selectedColor['attribute:img:id']
        ? selectedColor['attribute:img:id']
        : noImage
      : placeholderSrc;

    const selectedItemInfo = (
      <React.Fragment>
        <section style={{ display: 'flex' }}>
          <div
            style={{
              alignSelf: 'center',
              backgroundSize: 'cover',
              width: 150,
              display: 'inline-block',
            }}
          >
            <img
              style={{ width: '100%', verticalAlign: 'middle' }}
              src={selectedColorImgSrc}
              data-qa="edit-color-modal-image"
            />
            <div style={{ fontSize: '0.7rem' }}>In Assortment</div>
            {this.props.item.type === CartItemType.existing ? this.buildExistingSwatches() : null}
          </div>
          <div
            style={{
              marginLeft: 30,
              marginTop: 3,
              display: 'flex',
              flexDirection: 'column',
              width: 300,
            }}
          >
            <div
              style={{
                width: '100%',
                marginTop: 40,
              }}
            >
              <div
                style={{ padding: '10px 0', fontSize: '0.9rem', display: 'block' }}
                data-qa="EditColorsSelectedSwatchText"
              >
                {`COLOR: ${selectedColor ? selectedColor.colorId : ''}`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {this.buildSwatches()}
                <Button
                  disabled={isNil(selectedColor) || this.getColors().length < 2}
                  className={styles.textButton}
                  onClick={this.removeColor}
                  style={{
                    fontSize: '0.8rem',
                    margin: '0 5px',
                  }}
                  data-qa="remove-color-btn"
                >
                  REMOVE
                </Button>
              </div>
            </div>
            <div
              style={{
                marginTop: 10,
                width: '100%',
                fontSize: '0.9rem',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {this.renderAddColorList()}
              <Button
                disabled={!this.state.selectedNewColor}
                className={styles.textButton}
                onClick={this.addColor}
                style={{
                  fontSize: '0.8rem',
                  margin: '0 5px',
                }}
                data-qa="add-color-btn"
              >
                ADD
              </Button>
            </div>
          </div>
        </section>
      </React.Fragment>
    );

    return (
      <div className={styles.container} data-qa="edit-color-modal-container">
        <div className={styles.titleContainer}>
          <span>Edit Colors</span>
        </div>
        <div className={styles.contentContainer} data-qa="edit-color-modal-content-container">
          <div className={styles.subtitleContainer}>
            <span className={styles.titleText} data-qa="edit-color-modal-title">
              {this.props.title}
            </span>
          </div>
          <div className={styles.bodyContainer}>{selectedItemInfo}</div>
          <div className={styles.footerContainer}>
            <SquareActionButton
              text="Update Styles"
              onClick={this.onClickUpdate}
              disabled={isNil(selectedColor)}
              dataQa="update-style-btn"
              style={{ marginRight: '10px' }}
            />
            <SquareActionButton
              text="Cancel"
              onClick={() => this.closeModal()}
              type="secondary"
              dataQa="cancel-edit-btn"
            />
          </div>
        </div>
      </div>
    );
  }
}

const ColorsE = withStyles(muistyles)(ColorsEdit);
export default connect((state: AppState) => {
  return {
    placeholderImgAttr: state.appConfig.tenantConfig.addBehavior?.placeholderImgAttr,
  };
})(ColorsE);
